// The signature on client estimates and change orders. Each person uploads their own (a photo or scan of it on white
// paper, or a PNG): the paper is made transparent and the picture trimmed to the ink, then it is kept privately under
// their account (signature.save). Only its owner's browser ever loads it, so nobody can sign with someone else's;
// the name printed under it is whatever is typed in "Signed by". The signer set on the server (canSign) also has the
// built-in signature, used until they upload their own.
import { h, useEffect, useRef, useState } from '../core/react.js';
import { T } from '../core/theme.js';
import { API } from '../core/api.js';

export function useMySignature() {
  const [sig, setSig] = useState(null);
  useEffect(() => { API.call('signature.get').then(r => setSig(r.signature)).catch(() => {}); }, []);
  return [sig, setSig];
}

const loadImage = file => new Promise((res, rej) => {
  const url = URL.createObjectURL(file), img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); res(img); };
  img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("That file can't be opened as a picture. Use a PNG or JPG.")); };
  img.src = url;
});
// The paper made transparent, the picture trimmed to the ink and at most 900 px wide, as a PNG.
export async function prepareSignature(file) {
  const img = await loadImage(file);
  const k = Math.min(1, 900 / img.width, 400 / img.height);
  const W = Math.max(1, Math.round(img.width * k)), H = Math.max(1, Math.round(img.height * k));
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const g = c.getContext('2d'); g.drawImage(img, 0, 0, W, H);
  const d = g.getImageData(0, 0, W, H), p = d.data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let i = 0; i < p.length; i += 4) {
    const light = Math.min(p[i], p[i + 1], p[i + 2]);
    if (light > 200) p[i + 3] = 0;                                    // paper
    else if (light > 150) p[i + 3] = Math.round(p[i + 3] * (200 - light) / 50);   // soft edges
    if (p[i + 3] > 20) { const n = i / 4, x = n % W, y = (n - x) / W; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) throw new Error("No signature found in that picture. Use a dark pen on white paper.");
  g.putImageData(d, 0, 0);
  const pad = 4, cw = Math.min(W, x1 - x0 + 1 + 2 * pad), ch = Math.min(H, y1 - y0 + 1 + 2 * pad);
  const out = document.createElement('canvas'); out.width = cw; out.height = ch;
  out.getContext('2d').drawImage(c, Math.max(0, x0 - pad), Math.max(0, y0 - pad), cw, ch, 0, 0, cw, ch);
  return { img: out.toDataURL('image/png'), w: cw, h: ch };
}

export function SignatureControl({ sig, setSig, builtIn, on, setOn, readOnly, flash }) {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const link = { fontSize: 11, color: T.navyL, textDecoration: 'underline', padding: 0 };
  async function upload(f) {
    if (!f) return;
    setBusy(true);
    try {
      const s = await prepareSignature(f);
      await API.call('signature.save', s);
      setSig(s); setOn(true);
    } catch (e) { flash(e.message); } finally { setBusy(false); if (file.current) file.current.value = ''; }
  }
  async function remove() {
    if (!window.confirm(builtIn ? 'Remove your uploaded signature? The built-in one is used instead.' : 'Remove your signature?')) return;
    try { await API.call('signature.delete'); setSig(null); } catch (e) { flash(e.message); }
  }
  const has = !!(sig || builtIn);
  return h("div", { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
    has && h("label", { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.slate, cursor: 'pointer' } },
      h("input", { type: 'checkbox', checked: on, onChange: e => setOn(e.target.checked) }), ' Add my signature'),
    sig && h("img", { src: sig.img, alt: 'Your signature', style: { maxWidth: 160, maxHeight: 48, objectFit: 'contain', alignSelf: 'flex-start', opacity: on ? 1 : 0.4 } }),
    !readOnly && h("div", { style: { display: 'flex', gap: 12, flexWrap: 'wrap' } },
      h("button", { type: 'button', onClick: () => file.current?.click(), disabled: busy, style: link }, busy ? 'Saving…' : sig ? 'Change my signature' : 'Upload my signature'),
      sig && h("button", { type: 'button', onClick: remove, style: { ...link, color: T.mist } }, 'Remove')),
    !has && !readOnly && h("div", { style: { fontSize: 10.5, color: T.mist, lineHeight: 1.45 } }, 'A photo or scan of your signature in dark pen on white paper. The paper is removed; only you can use it.'),
    h("input", { ref: file, type: 'file', accept: 'image/png,image/jpeg', 'aria-label': 'Signature image', style: { display: 'none' }, onChange: e => upload(e.target.files[0]) }));
}
